import api from '@/lib/api';

/**
 * WhatsApp (Meta Cloud API) — Einstellungen.
 *
 * Eingehende Nachrichten werden serverseitig zu Tickets (channel 'whatsapp');
 * Agent-Antworten aus dem Ticket gehen automatisch über WhatsApp zurück.
 * Hier wird nur die Verbindung der Organisation verwaltet.
 */

export interface WhatsappConfig {
  organization_id?: string;
  /** Immer maskiert ("[HIDDEN]") oder null — Klartext verlässt den Server nie. */
  access_token: string | null;
  phone_number_id: string | null;
  waba_id: string | null;
  /** Für die Webhook-Signaturprüfung (optional, empfohlen). Maskiert. */
  app_secret: string | null;
  /** Vom Server generiert — bei Meta als Verify-Token eintragen. */
  verify_token: string | null;
  display_number: string | null;
  is_active: boolean;
  last_test_at?: string | null;
  last_test_status?: string | null;
  last_test_error?: string | null;
}

/** Eine Unterhaltung für die Seitenleisten-Karte. */
export interface WaConversation {
  ticket_id: string;
  wa_number: string | null;
  /** Jüngste Nachricht (Vorschau). */
  body: string | null;
  direction: 'in' | 'out';
  created_at: string;
  subject: string | null;
  status: string;
  organization_id: string;
  organization_name: string;
  /** Antwortfenster (24 h nach letzter EINGEHENDER Nachricht) noch offen? */
  window_open: boolean;
}

export interface WaConversations {
  configured: boolean;
  items: WaConversation[];
}

export interface WhatsappSettings {
  connected: boolean;
  enabled: boolean;
  config: WhatsappConfig;
  /** Bei Meta als Callback-URL eintragen. */
  webhookUrl: string;
}

const whatsappService = {
  /** Seitenleiste: letzte Unterhaltungen + Konfigurationsstatus. */
  async conversations(): Promise<WaConversations> {
    const { data } = await api.get('/whatsapp/conversations');
    return data.data;
  },

  /** Schnellantwort — läuft über den normalen Ticket-Reply (Route erkennt
      den WhatsApp-Kanal und sendet über die Cloud API). */
  async quickReply(ticketId: string, content: string): Promise<void> {
    await api.post(`/tickets/${ticketId}/reply`, { content, sendEmail: true });
  },

  async getSettings(organizationId?: string): Promise<WhatsappSettings> {
    const { data } = await api.get('/whatsapp/settings', { params: organizationId ? { organizationId } : {} });
    return data.data;
  },

  async saveSettings(payload: Partial<WhatsappConfig>, organizationId?: string): Promise<WhatsappSettings> {
    const { data } = await api.put('/whatsapp/settings', { ...payload, ...(organizationId ? { organizationId } : {}) });
    return data.data;
  },

  async testSettings(organizationId?: string): Promise<{ success: boolean; error: string | null; displayNumber?: string; verifiedName?: string }> {
    const { data } = await api.post('/whatsapp/settings/test', organizationId ? { organizationId } : {});
    return data.data;
  },
};

export default whatsappService;
